import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NGXLogger } from 'ngx-logger';
import { Observable, combineLatest, map, merge, of, startWith } from 'rxjs';

import { List, ListType } from 'models';
import { valueIsInList } from 'src/app/common/form-validators';
import { ListsService } from 'src/app/state/lists/lists.service';

import { OverlayContainer } from '@angular/cdk/overlay';
import {
  MatAutocompleteActivatedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { assertExists } from 'common';
import { filterList } from 'src/app/common/utility';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { FieldBaseComponent } from '../field-component.base.component';
import { ListFieldBaseComponent } from '../list-field-base.component';

/** Dropdown Field Component. */
@UntilDestroy()
@Component({
  selector: 'app-dropdown-field',
  templateUrl: './dropdown-field.component.html',
  styleUrls: ['./dropdown-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DropdownFieldComponent
  extends FieldBaseComponent
  implements OnInit, AfterViewInit, ListFieldBaseComponent
{
  /**
   * Alternate label for the mat-form-field.
   *
   * The field.name will be used if this is not provided.
   */
  @Input()
  alternateFormLabel: string;
  /**
   * Disables the list validator.
   *
   * This is really only useful on large lists that convert to dropdowns because normal dropdowns don't let you enter values.
   */
  disableListValidator = input(false, { transform: booleanAttribute });
  /**
   * Primary list form control.
   *
   * This only applies to dynamic list fields.
   *
   * If not set we attempt to get it from the parent form group.
   */
  @Input('list-primary-filter-control')
  primaryListFilterControl?: UntypedFormControl;
  /**
   * Secondary list form control.
   *
   * This only applies to dynamic list fields.
   *
   * If not set we attempt to get it from the parent form group.
   */
  @Input('list-secondary-filter-control')
  secondaryFilterControl?: UntypedFormControl;
  /** Autocomplete trigger for large lists. */
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger: MatAutocompleteTrigger;
  /** Input element reference. */
  @ViewChild('input')
  inputElement: ElementRef;
  /** Select element reference. */
  @ViewChild('select')
  selectElement: MatSelect;
  /**
   * Classes to be passed to the mat-select panelClass property.
   */
  matSelectPanelClassInput = input<string | string[]>(undefined, {
    alias: 'matSelectPanelClass',
  });

  /** Class string to be added to the list component's class attribute. */
  matSelectPanelClass = computed(() => {
    const panelClass = this.matSelectPanelClassInput();
    if (!panelClass) return ''; // There is no class so just return an empty string.

    // There is a panel class. Now see if it's an array.
    if (Array.isArray(panelClass)) {
      // It is an array so join it with a space for a typical class directive.
      return panelClass.join(' ');
    }

    // The panelClass is a string so just return it.
    return panelClass;
  });

  /** Observable list of filtered values. */
  filteredValues$: Observable<string[]>;
  /** Field list or undefined if the list has not yet loaded. */
  list: List | undefined;
  /** List types. */
  listType: ListType;

  private currentlyActiveTypeaheadListValue?: string;
  /** Indicates whether this field should immediately focus after a list is loaded. */
  private focusOnLoad = false;

  constructor(
    translate: TranslocoService,
    private applicationQuery: ApplicationQuery,
    private lists: ListsService,
    private overlayContainer: OverlayContainer,
    private logger: NGXLogger
  ) {
    super(translate);
  }

  /**
   * Get the label for the mat-form-field.
   *
   * @returns The label string for the mat-form-field
   */
  get fieldLabel(): string {
    return this.alternateFormLabel || this.field().name;
  }

  /**
   * Determines if the list is large.
   *
   * @returns True if the list exceeds 1000 entries.
   */
  get isLargelist(): boolean {
    return (
      (this.list?.values.length ?? 0) >
      this.applicationQuery.dropdownToTypeaheadConversionThreshold
    );
  }

  /** @inheritdoc */
  closeListPanel(): void {
    if (this.isLargelist) {
      this.autocompleteTrigger.closePanel();
    } else {
      this.selectElement.close();
    }
  }

  /** @inheritdoc */
  focus(): void {
    if (!this.list) {
      this.focusOnLoad = true;
      return;
    }
    if (this.isLargelist) {
      this.inputElement.nativeElement.focus();
      this.inputElement.nativeElement.select();
    } else {
      this.selectElement.focus();
    }
  }

  /**
   * Load the dynamic list using primary and secondary filters.
   *
   * @param primaryFilter Primary filter.
   * @param secondaryFilter Secondary filter.
   */
  loadDynamicList(primaryFilter: string, secondaryFilter: string): void {
    this.list = undefined;
    this.lists
      .getDynamicList(this.field().id, primaryFilter, secondaryFilter)
      .subscribe((list) => {
        this.list = list;
        if (!this.list.values.includes(this.control().value)) {
          // Force the value back to empty if it is not longer in the list.
          this.control().setValue('');
        }
        this.control().updateValueAndValidity();
        if (this.focusOnLoad) {
          this.focusOnLoad = false;
          // Force the focus to the control until the next render cycle.
          setTimeout(() => this.focus());
        }
      });
  }

  ngAfterViewInit(): void {
    this.listenForList();
    this.filteredValues$ = this.control().valueChanges.pipe(
      startWith(''),
      map((value) => filterList(value, this.list?.values ?? [], 100))
    );
  }

  ngOnInit(): void {
    this.addValidators();
    if (
      this.field().list.listId === 0 &&
      this.field().list.primary === 0 &&
      this.field().list.secondary === 0
    ) {
      throw new Error('Field does not contain a list.');
    }

    if (!this.disableListValidator()) {
      // Add is in list validator
      this.control().addValidators(valueIsInList(() => this.list?.values));
    }
  }

  /**
   * Handler for the auto complete's active list item event.
   *
   * @param event AUtocomplete option activation event.
   */
  onActivateTypeaheadListItem(event: MatAutocompleteActivatedEvent): void {
    this.currentlyActiveTypeaheadListValue = event.option?.value;
  }

  /** @inheritdoc */
  onBlur(event: FocusEvent): void {
    if (this.currentlyActiveTypeaheadListValue) {
      this.control().setValue(this.currentlyActiveTypeaheadListValue);
    }

    if (
      !this.shouldBlur(event) ||
      (this.isLargelist && this.autocompleteTrigger.panelOpen)
    )
      return;

    this.fieldBlurred.emit(this.field());
  }

  /**
   * Handler for the escape keydown event.
   *
   * @param event Keyboard event.
   */
  onEscapeTypeaheadInput(event: Event): void {
    this.currentlyActiveTypeaheadListValue = undefined;
  }

  /** @inheritdoc */
  onFocus(): void {
    this.fieldFocused.emit(this.field());
  }

  /**
   * Handler for the select change event.
   */
  onSelectionChange(): void {
    if (this.field().multiValue) {
      const mvFieldMenu = this.mvFieldMenu();
      assertExists(
        mvFieldMenu,
        'mvFieldMenu is required for multi-value fields.'
      );
      mvFieldMenu.addMultiValue.emit({ append: true });
    }
  }

  /** Handler for the mat-select closed event. */
  onSelectClosed(): void {
    // Remove  overlay class used to apply conditional styling.
    const overlayContainerElement = this.overlayContainer.getContainerElement();
    overlayContainerElement.classList.remove('field-select-overlay');
  }

  /** Handler for the mat-select open event. */
  onSelectOpen(): void {
    // Add a class to the overlay so that styles can be added elsewhere such as in the styles.scss file.
    const overlayContainerElement = this.overlayContainer.getContainerElement();
    overlayContainerElement.classList.add('field-select-overlay');
  }

  /**
   * Custom compare function for mat-select that checks if the selected value is in the list values regardless of casing.
   *
   * @param listValue Value in the list.
   * @param selectedValue The currently selected value.
   * @returns True if the selected value is in the list.
   */
  selectedValueIsInList(listValue: string, selectedValue: string): boolean {
    return listValue?.toLowerCase() === selectedValue?.toLowerCase(); // The types say neither of these values can be undefined but it seems as though one or both of them can sometimes end up being undefined.
  }

  /** @inheritdoc */
  shouldBlur(event: FocusEvent): boolean {
    return (
      super.shouldBlur(event) &&
      (!this.isLargelist || !this.autocompleteTrigger.panelOpen)
    );
  }

  private listenForControlChanges(): void {
    // Listen to changes to the control so we can be sure the select always closes when value changes.
    this.control().valueChanges.subscribe(() => {
      this.selectElement?.close();
      if (this.selectElement) {
        // This is a true dropdown with a mat-select so ensure it is closed after value is chosen.
        this.selectElement.close();
        // Ensure the dropdown field is still focused after closing the selection panel.
        this.selectElement.focus();
      }
    });
  }

  private listenForDynamicList(): void {
    const control = this.control();
    // If there is no parent we can't do anything here.
    if (!control.parent) {
      return;
    }

    // Gets the primary list field control
    const primaryFilterControl = this.primaryListFilterControl
      ? this.primaryListFilterControl
      : control.parent.get(this.field().list.primary.toString());
    if (!primaryFilterControl) {
      this.logger.error(
        'Unable to retrieve primary filter control for dynamic list.'
      );
      return;
    }

    // Store the initial primary value so we can load the initial list
    const initialPrimaryValue = primaryFilterControl.value;

    if (!initialPrimaryValue) {
      this.control().disable();
    }
    let secondaryValue$: Observable<any>;
    let initialSecondaryValue = '';

    // Merge the intial value and any changes to the value into a single observable value
    const primaryValue$ = merge(
      of(initialPrimaryValue),
      primaryFilterControl.valueChanges.pipe(untilDestroyed(this))
    );

    let secondaryFilterControl: AbstractControl<any, any> | null = null;

    if (this.field().list.secondary > 0) {
      secondaryFilterControl = this.secondaryFilterControl
        ? this.secondaryFilterControl
        : control.parent.get(this.field().list.secondary.toString());
      if (!secondaryFilterControl) {
        this.logger.error(
          'Unable to retrieve primary filter control for dynamic list.'
        );
        return;
      }

      // Store the initial secondary value if needed.
      initialSecondaryValue = secondaryFilterControl.value;

      if (!initialSecondaryValue) {
        this.control().disable();
      }

      // Merge the intial value and any changes to the value into a single observable value
      secondaryValue$ = merge(
        of(initialSecondaryValue),
        secondaryFilterControl.valueChanges.pipe(untilDestroyed(this))
      );
    } else {
      // If there is no secondary filter, just return an empty observable.
      secondaryValue$ = of('');
    }

    // Listen for changes to primary and secondary filter controls.
    combineLatest([primaryValue$, secondaryValue$]).subscribe(
      ([primaryValue, secondaryValue]) => {
        const primaryOrSecondaryInvalid =
          primaryFilterControl.invalid || secondaryFilterControl?.invalid;
        if (
          !primaryValue ||
          (this.field().list.secondary && !secondaryValue) ||
          primaryOrSecondaryInvalid
        ) {
          // Disable the control if there should be a value in a primary or secondary control but there isn't.
          this.control().disable();
        } else {
          this.control().enable();
        }
        this.loadDynamicList(primaryValue, secondaryValue);
      }
    );
  }

  private listenForList(): void {
    if (this.field().list.primary > 0 || this.field().list.secondary > 0) {
      this.listenForDynamicList();
    } else {
      this.list = undefined;
      this.lists
        .get(this.field().list.listId)
        .pipe(untilDestroyed(this))
        .subscribe((list) => {
          this.list = list;
          // Force validation to ensure validator applies
          this.control().updateValueAndValidity();
          this.listenForControlChanges();
          if (this.focusOnLoad) {
            this.focusOnLoad = false;
            // Force the focus to the control until the next render cycle.
            setTimeout(() => this.focus());
          }
        });
    }
  }
}
